import React, { useState } from 'react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import AuthLayout from '@/components/auth/AuthLayout';
import AuthHeader from '@/components/auth/AuthHeader';
import FormField from '@/components/auth/FormField';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const { resetPassword } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const { error } = await resetPassword(email);

            if (error) {
                toast({
                    title: "Request failed",
                    description: error.message,
                    variant: "destructive",
                });
            } else {
                setSubmitted(true);
                toast({
                    title: "Check your email",
                    description: "We've sent a password reset link to your email address.",
                });
            }
        } catch {
            toast({
                title: "An error occurred",
                description: "Please try again later.",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <AuthLayout>
                <div className="space-y-6">
                    <AuthHeader />

                    <h2 className="text-center text-[28px] font-semibold text-black">
                        Check your email
                    </h2>

                    <div className="space-y-4">
                        <p className="text-center text-gray-600">
                            We've sent a password reset link to{' '}
                            <span className="font-semibold text-black">{email}</span>
                        </p>

                        <p className="text-center text-gray-600">
                            Click the link in the email to reset your password. The link will expire in 24 hours.
                        </p>

                        <div className="pt-4">
                            <button
                                onClick={() => navigate('/auth')}
                                className="w-full h-12 rounded-lg bg-[#7C3AED] text-white text-lg font-semibold hover:bg-[#6D28D9] transition-colors"
                            >
                                Back to Sign In
                            </button>
                        </div>

                        <p className="text-center text-sm text-gray-500">
                            Didn't receive the email?{' '}
                            <button
                                onClick={() => {
                                    setSubmitted(false);
                                    setEmail('');
                                }}
                                className="text-[#7C3AED] font-medium hover:underline"
                            >
                                Try again
                            </button>
                        </p>
                    </div>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <div className="space-y-6">
                <button
                    onClick={() => navigate('/auth')}
                    className="flex items-center text-gray-600 hover:text-black transition-colors mb-4"
                >
                    <ArrowLeft size={20} className="mr-2" />
                    <span className="text-sm font-medium">Back</span>
                </button>

                <AuthHeader />

                <h2 className="text-center text-[28px] font-semibold text-black">
                    Reset your password
                </h2>

                <p className="text-center text-gray-600">
                    Enter the email address associated with your account and we'll send you a link to reset your password.
                </p>

                <form className="space-y-5" onSubmit={handleSubmit}>
                    <FormField
                        label="Email Address"
                        type="email"
                        placeholder="Enter your business email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full h-12 rounded-lg bg-[#7C3AED] text-white text-lg font-semibold hover:bg-[#6D28D9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <LoadingSpinner size="small" iconClassName="text-white" label="Sending..." />
                        ) : (
                            'Send Reset Link'
                        )}
                    </button>
                </form>

                <p className="text-center text-sm text-gray-500">
                    Remember your password?{' '}
                    <button
                        onClick={() => navigate('/auth')}
                        className="text-[#7C3AED] font-medium hover:underline"
                    >
                        Sign In
                    </button>
                </p>
            </div>
        </AuthLayout>
    );
};

export default ForgotPassword;
